import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileUp, Loader2, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminReportUpload = () => {
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reports, setReports] = useState([]);

  const load = () =>
    axios.get(`${API}/admin/reports`, { withCredentials: true })
      .then((r) => setReports(r.data.reports))
      .catch(() => {});

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!file) {
      toast.error("Choose a PDF first.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("customer_email", email);
      fd.append("title", title);
      fd.append("file", file);
      await axios.post(`${API}/admin/reports`, fd, { withCredentials: true });
      toast.success("Report uploaded — your customer can now download it.");
      form.reset();
      setEmail(""); setTitle(""); setFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-3xl border border-white/15 bg-[#0F1B31]/80 p-8" data-testid="admin-report-upload">
      <p className="font-mono-label text-xs uppercase tracking-[0.3em] text-slate-400 mb-6 flex items-center gap-2">
        <FileUp className="h-4 w-4" /> Send a Monthly Report
      </p>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input
          type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="Customer email" data-testid="report-customer-email-input"
          className="rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40"
        />
        <input
          type="text" required value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Report title (e.g. September 2026 Audit)" data-testid="report-title-input"
          className="rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40"
        />
        <label className="sm:col-span-2 flex items-center justify-center gap-3 rounded-xl border border-dashed border-white/25 bg-white/[0.02] px-4 py-6 text-sm text-slate-400 cursor-pointer hover:border-white/50 hover:text-white transition-colors duration-300" data-testid="report-file-dropzone">
          <input
            type="file" accept="application/pdf" className="hidden" data-testid="report-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <FileText className="h-4 w-4" />
          {file ? file.name : "Choose PDF report"}
        </label>
        <button
          type="submit" disabled={uploading} data-testid="report-upload-button"
          className="sm:col-span-2 inline-flex items-center justify-center gap-3 rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-[#060D1A] transition-colors duration-300 hover:bg-slate-300 disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
          Upload Report
        </button>
      </form>
      {reports.length > 0 && (
        <div className="mt-6 space-y-2" data-testid="admin-reports-list">
          {reports.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-4 rounded-xl border border-white/10 px-4 py-3 text-sm">
              <span className="text-slate-300 truncate">{r.title}</span>
              <span className="text-slate-500 whitespace-nowrap">{r.customer_email}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
